import React, { useState, /* useRef, */ useEffect, memo, useCallback } from 'react';
import './Map.css';
import { TOOLS } from './DrawingTools';
// Add import for history context
import { useHistory, COMMAND_TYPES } from '../../context/HistoryContext';
import ColorPicker from './ColorPicker';
import './ColorPicker.css';

// Extract TokenItem component and memoize it
const TokenItem = memo(({ 
  token, 
  isSelected, 
  isDragging,
  isEditMode, 
  onTokenClick, 
  onNameEdit, 
  onColorChange,
  onTokenMove,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onInitiativeChange,
  tokenGroup = null
}) => {
  // Local buffer to avoid pushing history on every digit; commit on blur/enter
  const [initiativeInput, setInitiativeInput] = useState(token.initiative ?? 15);
  useEffect(() => {
    setInitiativeInput(token.initiative ?? 15);
  }, [token.initiative]);
  return (
    <div 
      key={token.id}
      className={`token-item ${isSelected ? 'active' : ''} ${isDragging ? 'is-dragging' : ''} ${tokenGroup ? 'grouped' : ''} ${token.isPC !== false ? 'pc-token' : 'npc-token'}`}
      onClick={(e) => onTokenClick(token.id, e)}
      draggable="true"
      onDragStart={(e) => onDragStart(e, token.id)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver(e, token.id)}
      onDragLeave={onDragLeave}
      style={{
        '--token-color': token.fill || '#cccccc',
        '--token-color-rgb': (() => {
          const color = token.fill || '#cccccc';
          // Handle hex colors
          if (color.startsWith('#')) {
            const hex = color.replace('#', '');
            if (hex.length === 6) {
              const r = parseInt(hex.substr(0, 2), 16);
              const g = parseInt(hex.substr(2, 2), 16);
              const b = parseInt(hex.substr(4, 2), 16);
              return `${r}, ${g}, ${b}`;
            } else if (hex.length === 3) {
              const r = parseInt(hex.substr(0, 1).repeat(2), 16);
              const g = parseInt(hex.substr(1, 1).repeat(2), 16);
              const b = parseInt(hex.substr(2, 1).repeat(2), 16);
              return `${r}, ${g}, ${b}`;
            }
          }
          // Default fallback
          return '204, 204, 204'; // #cccccc in RGB
        })(),
        '--group-color': tokenGroup ? tokenGroup.color || '#007bff' : '#007bff'
      }}
    >
      {/* Token name - editable */}
      <span 
        className="token-name"
        contentEditable={false}
        suppressContentEditableWarning={true}
        title={tokenGroup ? `Token in group - Ctrl+click to multi-select, double-click to select whole group` : "Click to select token, Ctrl+click to multi-select"}
        onBlur={(e) => {
          e.target.contentEditable = false;
          onNameEdit(token.id, e.target.textContent);
        }}
        onClick={(e) => {
          e.stopPropagation();
          // Pass the event to handle Ctrl+click
          onTokenClick(token.id, e);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          
          // Check if this is for editing or group selection
          if (tokenGroup && !e.target.contentEditable) {
            // Double click on grouped token - select the whole group
            onTokenClick(token.id, { ...e, detail: 2 });
          } else {
            // Double click for editing
            e.target.contentEditable = true;
            e.target.focus();
            // Select all text for easy editing
            const range = document.createRange();
            range.selectNodeContents(e.target);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.target.blur();
          }
          if (e.key === 'Escape') {
            e.target.textContent = token.name || '';
            e.target.blur();
          }
        }}
      >
        {token.name || `Token ${token.id.substring(0, 4)}`}
      </span>
      
      {/* Token move buttons and initiative field */}
      <div className="token-move">
        <input 
          type="number" 
          className="token-initiative-input"
          value={initiativeInput}
          onChange={(e) => {
            e.stopPropagation();
            setInitiativeInput(e.target.value);
          }}
          onBlur={(e) => {
            e.stopPropagation();
            onInitiativeChange(token.id, initiativeInput);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              onInitiativeChange(token.id, initiativeInput);
              e.currentTarget.blur();
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          min="0"
          max="999"
          title="Initiative"
        />
        <div className="token-move-buttons">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onTokenMove(token.id, 'up');
            }}
            title="Move Up"
          >
            <i className="fas fa-chevron-up"></i>
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onTokenMove(token.id, 'down');
            }}
            title="Move Down"
          >
            <i className="fas fa-chevron-down"></i>
          </button>
        </div>
      </div>
    </div>
  );
});

const TokenManager = memo(({ 
  tokens = [], 
  selectedTokenIds = [], 
  onUpdateToken, 
  onDeleteToken, 
  onRestoreToken,
  toolOptions, 
  onOptionChange,
  onToolSelectRequest,
  isEditMode = true,
  onTokenSelect,
  activeTool,
  onToggleTokenGroup,
  onChangeGroupColor,
  tokenGroups = [],
  fovAdapter = null,
  groupLightingEnabled = false,
  onToggleGroupLighting
}) => {
  // Add history context
  const { pushCommand } = useHistory();
  
  // State for drag and drop functionality
  const [displayedTokens, setDisplayedTokens] = useState(tokens);
  const [draggedTokenId, setDraggedTokenId] = useState(null);

  // State for centralized color picker
  const [showTokenColorPicker, setShowTokenColorPicker] = useState(false);
  const [tokenColorAnchor, setTokenColorAnchor] = useState(null);


  
  // Helper function to check if a token is visible to any PC
  const isTokenVisibleToAnyPC = useCallback((targetToken, fovAdapter = null) => {
    // PCs are always visible
    if (targetToken.isPC !== false) return true;
    
    // NPCs are visible if they're in vision range of any PC AND there's line of sight
    const pcTokens = tokens.filter(token => token.isPC !== false);
    
    return pcTokens.some(pcToken => {
      const dx = targetToken.x - pcToken.x;
      const dy = targetToken.y - pcToken.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const visionRadius = pcToken.visionRadius || 200;
      
      // First check if within range
      if (distance > visionRadius) return false;
      
      // If FOV adapter is available, check line of sight
      if (fovAdapter && fovAdapter.enabled) {
        try {
          // Calculate visibility from PC position
          const visibilityPolygon = fovAdapter.calculateVisibility(
            pcToken.x, 
            pcToken.y, 
            visionRadius
          );
          
          // Update the FOV internal state
          fovAdapter.getFOV().visibilityPolygon = visibilityPolygon;
          fovAdapter.getFOV().origin = { x: pcToken.x, y: pcToken.y };
          
          // Check if the target token position is visible
          const isVisible = fovAdapter.isPointVisible(targetToken.x, targetToken.y);
          
          // FIXED: Return the actual FOV result instead of always true
          return isVisible;
        } catch (error) {
          console.warn('Error checking FOV visibility, falling back to distance check:', error);
          // FIXED: Fall back to distance-only check (true) only on error, but be more conservative
          // If there's an error with FOV but we know there should be walls, be more careful
          const wallsCount = fovAdapter.getFOV()?.walls?.length || 0;
          if (wallsCount > 0) {
            // If there are walls but FOV failed, assume blocked for safety
            console.warn('[TokenManager] FOV failed but walls exist, assuming blocked for safety');
            return false;
          }
          // If no walls, distance check is sufficient
          return true;
        }
      }
      
      // Fallback to distance-only check if no FOV adapter (within range = visible)
      return true;
    });
  }, [tokens]);
  
  // Update displayed tokens when the tokens prop changes and not during dragging
  useEffect(() => {
    if (!draggedTokenId) {
      let filteredTokens = [...tokens];
      
      // In play mode, filter out NPCs that are not visible to any PC
      if (!isEditMode) {
        filteredTokens = filteredTokens.filter(token => isTokenVisibleToAnyPC(token, fovAdapter));
      }
      
      // Sort tokens by zIndex in descending order
      const sortedTokens = filteredTokens.sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));
      setDisplayedTokens(sortedTokens);
    }
  }, [tokens, draggedTokenId, isEditMode, isTokenVisibleToAnyPC, fovAdapter]);
  
  // Handle token selection - memoize with useCallback
  const handleTokenClick = useCallback((tokenId, event) => {
    if (onTokenSelect) {
      const ctrlKey = event?.ctrlKey || event?.metaKey || false;
      const isDoubleClick = event?.detail === 2; // Detect double click
      onTokenSelect(tokenId, ctrlKey, false, isDoubleClick);
    }
  }, [onTokenSelect]);
  
  // Handle token name edit - memoize with useCallback
  const handleNameEdit = useCallback((tokenId, newName) => {
    if (newName.trim() && onUpdateToken) {
      const token = tokens.find(t => t.id === tokenId);
      if (token && token.name !== newName.trim()) {
        // Save previous state for undo
        const previousState = { ...token };
        const newState = { ...token, name: newName.trim() };
        
        // Push command to history
        pushCommand({
          type: COMMAND_TYPES.UPDATE_TOKEN,
          targetId: tokenId,
          previousState,
          newState,
          selectedTokenIds: selectedTokenIds // Preserve selection state
        });
        
        onUpdateToken(tokenId, { name: newName.trim() });
      }
    }
  }, [onUpdateToken, tokens, selectedTokenIds, pushCommand]);
  
  // Handle token color change - memoize with useCallback
  const handleColorChange = useCallback((tokenId, newColor) => {
    if (onUpdateToken) {
      const token = tokens.find(t => t.id === tokenId);
      if (token && token.fill !== newColor) {
        // Save previous state for undo
        const previousState = { ...token };
        const newState = { ...token, fill: newColor };
        
        // Push command to history
        pushCommand({
          type: COMMAND_TYPES.UPDATE_TOKEN,
          targetId: tokenId,
          previousState,
          newState,
          selectedTokenIds: selectedTokenIds // Preserve selection state
        });
        
        onUpdateToken(tokenId, { fill: newColor });
      }
    }
  }, [onUpdateToken, tokens, selectedTokenIds, pushCommand]);
  
  // Handle centralized token color picker
  const handleTokenColorClick = useCallback((e) => {
    if (selectedTokenIds.length === 0) return;
    e.stopPropagation();
    
    // Ensure anchor element is valid
    const anchorElement = e.currentTarget;
    if (!anchorElement || !anchorElement.isConnected) {
      console.warn('TokenManager ColorPicker: Invalid anchor element');
      return;
    }
    
    setTokenColorAnchor(anchorElement);
    setShowTokenColorPicker(true);
  }, [selectedTokenIds]);

  const handleTokenColorChange = useCallback((newColor) => {
    if (selectedTokenIds.length === 0) return;
    
    // Update color for all selected tokens
    const previousStates = [];
    const newStates = [];
    
    selectedTokenIds.forEach(tokenId => {
      const token = tokens.find(t => t.id === tokenId);
      if (token && token.fill !== newColor) {
        const previousState = { ...token };
        const newState = { ...token, fill: newColor };
        
        previousStates.push({ ...previousState, id: tokenId });
        newStates.push({ ...newState, id: tokenId });
        
        onUpdateToken(tokenId, { fill: newColor });
      }
    });
    
    // Push single command to history for all updates
    if (previousStates.length > 0) {
      pushCommand({
        type: 'BATCH_UPDATE_TOKENS',
        previousStates,
        newStates,
        selectedTokenIds: selectedTokenIds
      });
    }
  }, [selectedTokenIds, tokens, onUpdateToken, pushCommand]);

  const handleTokenColorClose = useCallback(() => {
    // Add a small delay to prevent race conditions
    setTimeout(() => {
      setShowTokenColorPicker(false);
      setTokenColorAnchor(null);
    }, 10);
  }, []);
  
  // Handle token move up/down - memoize with useCallback
  const handleTokenMove = useCallback((tokenId, direction) => {
    // Find the token in the displayed tokens (which are sorted by zIndex)
    const sortedTokens = [...displayedTokens].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));
    const currentIndex = sortedTokens.findIndex(t => t.id === tokenId);
    
    if (currentIndex === -1) return;
    
    let targetIndex;
    if (direction === 'up' && currentIndex > 0) {
      targetIndex = currentIndex - 1;
    } else if (direction === 'down' && currentIndex < sortedTokens.length - 1) {
      targetIndex = currentIndex + 1;
    } else {
      return; // Nothing to do - already at the edge
    }
    
    // Get the tokens to swap
    const currentToken = sortedTokens[currentIndex];
    const targetToken = sortedTokens[targetIndex];
    
    // Save previous states for undo
    const previousStates = [
      { ...currentToken },
      { ...targetToken }
    ];
    
    // Swap their zIndex values
    const currentZIndex = currentToken.zIndex || 0;
    const targetZIndex = targetToken.zIndex || 0;
    
    onUpdateToken(currentToken.id, { zIndex: targetZIndex });
    onUpdateToken(targetToken.id, { zIndex: currentZIndex });
    
    // Push command to history for the move operation
    pushCommand({
      type: COMMAND_TYPES.MOVE_TOKEN,
      targetId: tokenId,
      previousStates,
      newStates: [
        { ...currentToken, zIndex: targetZIndex },
        { ...targetToken, zIndex: currentZIndex }
      ],
      selectedTokenIds: selectedTokenIds // Preserve selection state
    });
  }, [displayedTokens, onUpdateToken, selectedTokenIds, pushCommand]);
  
  // Handle adding a new token
  const handleAddToken = () => {
    // Allow toggling token tool in both edit and play mode
    if (onToolSelectRequest) {
      // Toggle token tool: If already active, deactivate by setting SELECT tool active
      if (activeTool === TOOLS.TOKEN) {
        onToolSelectRequest('select');
      } else {
        // Otherwise, set token tool active
        onToolSelectRequest('token');
      }
    }
  };
  
  // Handle removing selected tokens
  const handleRemoveToken = () => {
    if (selectedTokenIds.length > 0 && onDeleteToken) {
      // Build a single batch command so one Undo restores all
      const commands = [];
      selectedTokenIds.forEach(tokenId => {
        const tokenToDelete = tokens.find(t => t.id === tokenId);
        if (tokenToDelete) {
          commands.push({
            type: COMMAND_TYPES.DELETE_TOKEN,
            targetId: tokenId,
            previousState: { ...tokenToDelete },
            selectedTokenIds: [...selectedTokenIds],
            nextSelectedTokenIds: []
          });
        }
      });

      if (commands.length > 0) {
        pushCommand({
          type: COMMAND_TYPES.BATCH,
          commands,
          description: `Delete ${commands.length} tokens`
        });

        // Perform the actual deletions
        commands.forEach(cmd => onDeleteToken(cmd.targetId));
      }
    }
  };

  // Handle uploading/removing image for selected tokens
  const handleUploadTokenImage = () => {
    if (selectedTokenIds.length === 0) return;
    
    // Check if any selected tokens have images
    const tokensWithImages = selectedTokenIds.filter(tokenId => {
      const token = tokens.find(t => t.id === tokenId);
      return token && token.image && token.image.src;
    });
    
    // If some tokens have images, remove them first
    if (tokensWithImages.length > 0) {
      const previousStates = [];
      const newStates = [];
      
      selectedTokenIds.forEach(tokenId => {
        const token = tokens.find(t => t.id === tokenId);
        if (token) {
          const previousState = { ...token };
          const newState = { ...token };
          
          // Remove image if it exists
          if (token.image && token.image.src) {
            delete newState.image;
          }
          
          previousStates.push({ ...previousState, id: tokenId });
          newStates.push({ ...newState, id: tokenId });
          
          // Update the token
          onUpdateToken(tokenId, { image: null });
        }
      });
      
      // Push command to history for removing images
      if (previousStates.length > 0) {
        pushCommand({
          type: 'BATCH_UPDATE_TOKENS',
          previousStates,
          newStates,
          selectedTokenIds: selectedTokenIds
        });
      }
      return; // Exit after removing images
    }
    
    // If no tokens have images, proceed with upload
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const imageUrl = event.target.result;
          const img = new Image();
          img.onload = () => {
            // Create a canvas to resize the image
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Calculate new dimensions (max 150px)
            const maxSize = 150;
            let { width, height } = img;
            
            if (width > height) {
              if (width > maxSize) {
                height = (height * maxSize) / width;
                width = maxSize;
              }
            } else {
              if (height > maxSize) {
                width = (width * maxSize) / height;
                height = maxSize;
              }
            }
            
            // Set canvas size and draw resized image
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            
            // Get the resized image as data URL
            const resizedImageUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            // Prepare batch update for all selected tokens
            const previousStates = [];
            const newStates = [];
            
            selectedTokenIds.forEach(tokenId => {
              const token = tokens.find(t => t.id === tokenId);
              if (token) {
                // Save previous state for undo
                const previousState = { ...token };
                const newState = { 
                  ...token, 
                  image: {
                    src: resizedImageUrl,
                    width: width,
                    height: height,
                    originalAspectRatio: width / height
                  }
                };
                
                previousStates.push({ ...previousState, id: tokenId });
                newStates.push({ ...newState, id: tokenId });
                
                // Update the token
                onUpdateToken(tokenId, { 
                  image: {
                    src: resizedImageUrl,
                    width: width,
                    height: height,
                    originalAspectRatio: width / height
                  }
                });
              }
            });
            
            // Push single command to history for all updates
            if (previousStates.length > 0) {
              pushCommand({
                type: 'BATCH_UPDATE_TOKENS',
                previousStates,
                newStates,
                selectedTokenIds: selectedTokenIds
              });
            }
          };
          img.src = imageUrl;
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  // Handle token size change - now works for multiple selected tokens
  const handleTokenSizeChange = (newSize) => {
    if (selectedTokenIds.length === 0) return;
    
    const size = parseInt(newSize, 10);
    if (!isNaN(size) && size > 0) {
      const newRadius = size / 2;
      
      // Prepare batch update for all selected tokens
      const previousStates = [];
      const newStates = [];
      
      selectedTokenIds.forEach(tokenId => {
        const token = tokens.find(t => t.id === tokenId);
        if (token) {
          const previousState = { ...token };
          const newState = { 
            ...token, 
            radius: newRadius,
            // Also update vision radius proportionally if it was default
            visionRadius: token.visionRadius === (token.radius * 5) ? newRadius * 5 : token.visionRadius
          };
          
          previousStates.push({ ...previousState, id: tokenId });
          newStates.push({ ...newState, id: tokenId });
          
          // Update the token
          onUpdateToken(tokenId, { 
            radius: newRadius,
            visionRadius: token.visionRadius === (token.radius * 5) ? newRadius * 5 : token.visionRadius
          });
        }
      });
      
      // Push single command to history for all updates
      if (previousStates.length > 0) {
        pushCommand({
          type: 'BATCH_UPDATE_TOKENS',
          previousStates,
          newStates,
          selectedTokenIds: selectedTokenIds
        });
      }
    }
  };

  // Handle token initiative change - memoize with useCallback
  const handleInitiativeChange = useCallback((tokenId, newInitiative) => {
    const initiative = parseInt(newInitiative, 10);
    if (!isNaN(initiative) && onUpdateToken) {
      const token = tokens.find(t => t.id === tokenId);
      if (token && token.initiative !== initiative) {
        // Save previous state for undo
        const previousState = { ...token };
        const newState = { ...token, initiative: initiative };
        
        // Push command to history
        pushCommand({
          type: COMMAND_TYPES.UPDATE_TOKEN,
          targetId: tokenId,
          previousState,
          newState,
          selectedTokenIds: selectedTokenIds
        });
        
        onUpdateToken(tokenId, { initiative: initiative });
      }
    }
  }, [onUpdateToken, tokens, selectedTokenIds, pushCommand]);

  // Handle sorting tokens by initiative
  const handleSortByInitiative = useCallback(() => {
    if (displayedTokens.length <= 1) return;
    
    // Sort by initiative (descending - highest first), then by name as tiebreaker
    const sortedTokens = [...displayedTokens].sort((a, b) => {
      const aInit = a.initiative || 15;
      const bInit = b.initiative || 15;
      if (aInit !== bInit) {
        return bInit - aInit; // Higher initiative first
      }
      // Tiebreaker: alphabetical by name
      const aName = a.name || `Token ${a.id.substring(0, 4)}`;
      const bName = b.name || `Token ${b.id.substring(0, 4)}`;
      return aName.localeCompare(bName);
    });
    
    // Update zIndex based on new order
    const updatedTokens = sortedTokens.map((token, index) => ({
      ...token,
      zIndex: sortedTokens.length - 1 - index // Higher initiative gets higher zIndex
    }));
    
    // Update all tokens with their new zIndex
    updatedTokens.forEach(token => {
      onUpdateToken(token.id, { zIndex: token.zIndex });
    });
  }, [displayedTokens, onUpdateToken]);

  // Handle token vision radius change for the control in token-actions - now works for multiple selected tokens
  const handleTokenVisionRadiusChange = (newRadius) => {
    if (selectedTokenIds.length === 0) return;
    
    const radius = parseInt(newRadius, 10);
    if (!isNaN(radius) && onUpdateToken) {
      // Prepare batch update for all selected tokens
      const previousStates = [];
      const newStates = [];
      
      selectedTokenIds.forEach(tokenId => {
        const token = tokens.find(t => t.id === tokenId);
        if (token && token.visionRadius !== radius) {
          const previousState = { ...token };
          const newState = { ...token, visionRadius: radius };
          
          previousStates.push({ ...previousState, id: tokenId });
          newStates.push({ ...newState, id: tokenId });
          
          // Update the token
          onUpdateToken(tokenId, { visionRadius: radius });
        }
      });
      
      // Push single command to history for all updates
      if (previousStates.length > 0) {
        pushCommand({
          type: 'BATCH_UPDATE_TOKENS',
          previousStates,
          newStates,
          selectedTokenIds: selectedTokenIds
        });
      }
    }
  };

  // Handle PC/NPC toggle - now works for multiple selected tokens
  const handlePCNPCToggle = () => {
    if (selectedTokenIds.length === 0) return;
    
    // Check current state of first selected token to determine toggle direction
    const firstToken = tokens.find(t => t.id === selectedTokenIds[0]);
    const newIsPC = !(firstToken?.isPC !== false); // Default to true (PC) if not set
    
    // Prepare batch update for all selected tokens
    const previousStates = [];
    const newStates = [];
    
    selectedTokenIds.forEach(tokenId => {
      const token = tokens.find(t => t.id === tokenId);
      if (token) {
        const previousState = { ...token };
        const newState = { ...token, isPC: newIsPC };
        
        previousStates.push({ ...previousState, id: tokenId });
        newStates.push({ ...newState, id: tokenId });
        
        // Update the token
        onUpdateToken(tokenId, { isPC: newIsPC });
      }
    });
    
    // Push single command to history for all updates
    if (previousStates.length > 0) {
      pushCommand({
        type: 'BATCH_UPDATE_TOKENS',
        previousStates,
        newStates,
        selectedTokenIds: selectedTokenIds
      });
    }
  };

  // Drag and drop handlers - memoized with useCallback
  const handleDragStart = useCallback((e, tokenId) => {
    setDraggedTokenId(tokenId);
    // Select this token when starting to drag it
    if (onTokenSelect) {
      onTokenSelect(tokenId);
    }
    e.dataTransfer.effectAllowed = "move";
    
    // Create a custom drag image to ensure consistent visual feedback
    const draggedElement = e.currentTarget;
    const rect = draggedElement.getBoundingClientRect();
    
    // Create a clone for the ghost image
    const ghostElement = draggedElement.cloneNode(true);
    ghostElement.style.width = `${rect.width}px`;
    ghostElement.style.height = `${rect.height}px`;
    ghostElement.style.backgroundColor = 'var(--surface-color)';
    ghostElement.style.opacity = '0.7';
    ghostElement.style.position = 'absolute';
    ghostElement.style.top = '-1000px'; // Position off-screen
    ghostElement.style.border = '1px dashed var(--accent-color)';
    
    // Add to document body temporarily
    document.body.appendChild(ghostElement);
    
    // Set as drag image
    e.dataTransfer.setDragImage(ghostElement, rect.width / 2, rect.height / 2);
    
    // Remove after a slight delay
    setTimeout(() => {
      document.body.removeChild(ghostElement);
    }, 0);
  }, [onTokenSelect, setDraggedTokenId]);

  const handleDragEnd = useCallback((e) => {
    // Remove all drag-over indicators
    const allTokenElements = document.querySelectorAll('.token-item');
    allTokenElements.forEach(el => {
      el.classList.remove('drag-over');
    });
    
    if (draggedTokenId) {
      // Get the updated order of token IDs
      const newTokenIds = displayedTokens.map(token => token.id);
      
      // Create an array of tokens with updated zIndex values
      const updatedTokens = newTokenIds.map((id, index) => {
        const token = displayedTokens.find(t => t.id === id);
        return {
          ...token,
          zIndex: displayedTokens.length - 1 - index // Reverse order for zIndex (top items have higher zIndex)
        };
      });
      
      // Update all tokens with their new zIndex
      updatedTokens.forEach(token => {
        onUpdateToken(token.id, { zIndex: token.zIndex });
      });
    }
    
    // Important: Reset draggedTokenId last to avoid re-rendering issues
    setDraggedTokenId(null);
  }, [draggedTokenId, displayedTokens, onUpdateToken, setDraggedTokenId]);

  const handleDragOver = useCallback((e, targetTokenId) => {
    e.preventDefault();
    if (!draggedTokenId || draggedTokenId === targetTokenId) return;

    // Add visual indicator for drop target
    const allTokenElements = document.querySelectorAll('.token-item');
    allTokenElements.forEach(el => {
      el.classList.remove('drag-over');
    });
    
    // Find the target element and add drag-over class
    const targetElement = e.currentTarget;
    if (targetElement) {
      targetElement.classList.add('drag-over');
    }

    // Select the target token when dragging over it
    if (onTokenSelect && !selectedTokenIds.includes(targetTokenId)) {
      onTokenSelect(targetTokenId);
    }

    const currentOrder = [...displayedTokens];
    const draggedItemIndex = currentOrder.findIndex(t => t.id === draggedTokenId);
    const targetItemIndex = currentOrder.findIndex(t => t.id === targetTokenId);

    if (draggedItemIndex === -1 || targetItemIndex === -1 || draggedItemIndex === targetItemIndex) return;

    const draggedItem = currentOrder[draggedItemIndex];
    currentOrder.splice(draggedItemIndex, 1);
    currentOrder.splice(targetItemIndex, 0, draggedItem);
    
    setDisplayedTokens(currentOrder);
  }, [draggedTokenId, displayedTokens, onTokenSelect, selectedTokenIds, setDisplayedTokens]);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    
    // Remove all drag-over indicators
    const allTokenElements = document.querySelectorAll('.token-item');
    allTokenElements.forEach(el => {
      el.classList.remove('drag-over');
    });
  }, []);
  
  // Clean up drag-over indicators when drag leaves a token
  const handleDragLeave = useCallback((e) => {
    e.currentTarget.classList.remove('drag-over');
  }, []);
  

  
  return (
    <div className="token-manager">
      <div className="tokens-title">Tokens</div>
      
      <div className="token-actions">
        <button 
          className={`create-layer-btn add-token-btn ${activeTool === TOOLS.TOKEN ? 'active' : ''}`}
          onClick={handleAddToken}
          title="Add Token"
        >
          <span className="icon-group">
            <i className="fas fa-street-view"></i>
          </span>
        </button>
        
        <button 
          className={`upload-map-btn ${
            selectedTokenIds.length > 0 && selectedTokenIds.some(tokenId => {
              const token = tokens.find(t => t.id === tokenId);
              return token && token.image && token.image.src;
            }) ? 'remove-mode' : ''
          }`}
          onClick={handleUploadTokenImage}
          title={
            selectedTokenIds.length === 0 ? "Select tokens to upload/remove image" :
            selectedTokenIds.some(tokenId => {
              const token = tokens.find(t => t.id === tokenId);
              return token && token.image && token.image.src;
            }) ? 
            (selectedTokenIds.length > 1 ? `Remove images from ${selectedTokenIds.length} tokens` : "Remove token image") :
            (selectedTokenIds.length > 1 ? `Upload image to ${selectedTokenIds.length} tokens` : "Upload token image")
          }
          disabled={selectedTokenIds.length === 0}
        >
          <i className="fas fa-image"></i>
        </button>
        
        <div className="token-size-control" title={selectedTokenIds.length > 1 ? `Change size for ${selectedTokenIds.length} tokens` : selectedTokenIds.length === 1 ? "Token Size" : "Select tokens to change size"}>
          <span className="size-label"><i className="fas fa-expand-arrows-alt" title="Size"></i></span>
          <input
            type="number"
            min="10"
            max="200"
            step="5"
            value={selectedTokenIds.length >= 1 ? (tokens.find(t => t.id === selectedTokenIds[0])?.radius * 2 || 30) : 30}
            onChange={(e) => handleTokenSizeChange(e.target.value)}
            disabled={selectedTokenIds.length === 0}
            className="token-size-input"
          />
        </div>
        
        <div className="token-vision-control" title={selectedTokenIds.length > 1 ? `Change vision radius for ${selectedTokenIds.length} tokens` : selectedTokenIds.length === 1 ? "Token Vision Radius" : "Select tokens to change vision"}>
          <span className="vision-label"><i className="fas fa-eye" title="Vision Radius"></i></span>
          <input
            type="number"
            min="0"
            max="500"
            step="10"
            value={selectedTokenIds.length >= 1 ? (tokens.find(t => t.id === selectedTokenIds[0])?.visionRadius || 200) : 200}
            onChange={(e) => handleTokenVisionRadiusChange(e.target.value)}
            disabled={selectedTokenIds.length === 0}
            className="token-vision-input"
          />
        </div>
        
        <button 
          className="upload-map-btn" 
          onClick={handleSortByInitiative}
          title="Sort by Initiative"
          disabled={displayedTokens.length <= 1}
        >
          <i className="fas fa-sort"></i>
        </button>
        
        <button 
          className="upload-map-btn" 
          onClick={handleTokenColorClick}
          title={
            selectedTokenIds.length === 0 ? "Select tokens to change color" :
            selectedTokenIds.length > 1 ? `Change color for ${selectedTokenIds.length} tokens` : "Change token color"
          }
          disabled={selectedTokenIds.length === 0}
        >
          <i className="fas fa-palette"></i>
        </button>
        
        <button 
          className="link-layers-btn" 
          onClick={onToggleTokenGroup}
          onContextMenu={(e) => {
            e.preventDefault();
            // Only allow color change if there's a selected group
            if (selectedTokenIds.length > 0 && selectedTokenIds.some(tokenId => 
              tokenGroups.some(group => group.tokenIds.includes(tokenId))
            ) && onChangeGroupColor) {
              onChangeGroupColor(e.currentTarget);
            }
          }}
          title={
            selectedTokenIds.length > 0 && selectedTokenIds.some(tokenId => 
              tokenGroups.some(group => group.tokenIds.includes(tokenId))
            ) ? "Ungroup Selected Tokens | Right-click to change color" : "Group Selected Tokens"
          }
          disabled={selectedTokenIds.length === 0}
        >
          <i className={
            selectedTokenIds.length > 0 && selectedTokenIds.some(tokenId => 
              tokenGroups.some(group => group.tokenIds.includes(tokenId))
            ) ? "fas fa-unlink" : "fas fa-link"
          }></i>
        </button>

        <button 
          className={`upload-map-btn ${groupLightingEnabled ? 'active' : ''}`}
          onClick={onToggleGroupLighting}
          title={
            selectedTokenIds.length < 2 ? "Select at least 2 tokens for group lighting" :
            groupLightingEnabled ? "Disable group lighting (all selected tokens as light sources)" : "Enable group lighting (all selected tokens as light sources)"
          }
          disabled={selectedTokenIds.length < 2}
        >
          <i className="fas fa-lightbulb"></i>
        </button>
        
        <button 
          className="upload-map-btn" 
          onClick={handlePCNPCToggle}
          title={
            selectedTokenIds.length === 0 ? "Select tokens to toggle PC/NPC" :
            (() => {
              const firstToken = tokens.find(t => t.id === selectedTokenIds[0]);
              const isPC = firstToken?.isPC !== false; // Default to true (PC) if not set
              return selectedTokenIds.length > 1 ? 
                (isPC ? `Convert ${selectedTokenIds.length} tokens to NPC` : `Convert ${selectedTokenIds.length} tokens to PC`) :
                (isPC ? "Convert to NPC" : "Convert to PC");
            })()
          }
          disabled={selectedTokenIds.length === 0}
        >
          <i className={(() => {
            const firstToken = tokens.find(t => t.id === selectedTokenIds[0]);
            const isPC = firstToken?.isPC !== false; // Default to true (PC) if not set
            return isPC ? "fas fa-user" : "fas fa-robot";
          })()}></i>
        </button>
        
        <button 
          className="delete-layer-btn" 
          onClick={handleRemoveToken}
          title="Remove Selected Token"
          disabled={selectedTokenIds.length === 0}
        >
          <i className="fas fa-trash"></i>
        </button>
      </div>
      
      <div className="tokens-list" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
        {displayedTokens.length === 0 ? (
          <div className="no-token-selected">No tokens on the map</div>
        ) : (
          // Sort tokens by zIndex in descending order (highest zIndex at the top)
          [...displayedTokens].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0)).map(token => {
            // Find the group this token belongs to
            const tokenGroup = tokenGroups.find(group => group.tokenIds.includes(token.id));
            
            return (
              <TokenItem
                key={token.id}
                token={token}
                isSelected={selectedTokenIds.includes(token.id)}
                isDragging={draggedTokenId === token.id}
                isEditMode={isEditMode}
                onTokenClick={handleTokenClick}
                onNameEdit={handleNameEdit}
                onColorChange={handleColorChange}
                onTokenMove={handleTokenMove}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onInitiativeChange={handleInitiativeChange}
                tokenGroup={tokenGroup}
              />
            );
          })
        )}
      </div>
      
      {/* Centralized Color Picker */}
      <ColorPicker
        isOpen={showTokenColorPicker}
        onClose={handleTokenColorClose}
        initialColor={selectedTokenIds.length > 0 ? (tokens.find(t => t.id === selectedTokenIds[0])?.fill || '#cccccc') : '#cccccc'}
        onChange={handleTokenColorChange}
        anchorElement={tokenColorAnchor}
      />
    </div>
  );
});

export default TokenManager; 